import {
  useDebounceFn,
  useElementBounding,
  useElementSize,
} from "@vueuse/core";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import { MarkerType, Position, useVueFlow } from "@vue-flow/core";
import { stratify, tree } from "d3-hierarchy";
import uniq from "lodash/uniq";

import {
  OrchestrationStructureType,
  OrchestrationTemplateStatus,
} from "@/enums/orchestration";
import { extractLetters, get2dEmptyPosition } from "@/helpers/common";
import {
  ALL_OPTIONS_SSN_CONFIDENCE_LEVELS,
  COMMON_OPTIONS_GREATER_SSN_CONFIDENCE_LEVELS,
  COMMON_OPTIONS_LESSER_SSN_CONFIDENCE_LEVELS,
  DEFAULT_DO_TRIGGER,
  DO_SAVE_TEMPLATE_MODES,
  DO_TYPE_CONDITIONAL,
  GREATER_TYPES_SSN_CONFIDENCE_LEVELS,
  LESSER_TYPES_SSN_CONFIDENCE_LEVELS,
  SERVICE_CUSTOM_ATTRIBUTES,
  SERVICE_TRIGGER,
} from "@/helpers/constants";
import {
  OUTCOME_NODE_HEIGHT,
  STEP_NODE_HEIGHT,
  STEP_NODE_WIDTH,
  nodesGraphPositionAdjustments,
  nodesStructuredPositionAdjustments,
} from "@/helpers/constants/orchestration";
import {
  validateDataOrchestrationPayload,
  wouldCreateCycle,
} from "@/helpers/orchestration";

import { useNotification } from "./notifications";

import type { Position2D } from "@/models/common";
import type {
  ActiveConfigPanel,
  AddPositionToStepOrOutcomePayload,
  DataOrchestrationNode,
  DataOrchestrationNodeType,
  DataOrchestrationOutcome,
  IActiveConfigPanelConditional,
  IAttribute,
  IDataOrchestrationFlowOutcome,
  IDataService,
  IDataServiceService,
  IDataServices,
  IDoDraggable,
  IOrchestrationTemplate,
  IServiceAttribute,
  IStep,
} from "@/models/orchestration";
import type { NodeDragEvent } from "@vue-flow/core";
import type { Connection, Edge as VueFlowEdge } from "@vue-flow/core";
import type { ComputedRef, Ref } from "vue";

const getServiceAttribute = (
  serviceName: string,
  attribute: IAttribute
): IServiceAttribute => {
  return {
    key: attribute.id,
    name: attribute.name,
    meta: attribute.meta,
    service: serviceName,
    fetchesData: attribute.fetches_data,
  };
};

export const useDataOrchestration = () => {
  const { getters, commit } = useStore();

  const activeTemplate = computed<IOrchestrationTemplate>(
    () => getters["orchestration/activeTemplate"]
  );

  const activeStep = computed<IStep | null>(
    () => getters["orchestration/activeStep"]
  );

  const serviceInfo = computed<IDataService | undefined>(() =>
    getters["orchestration/serviceInfoByTitle"](activeStep.value?.title)
  );

  const activeStepProvider = computed<IDataServiceService | undefined>(() =>
    serviceInfo.value?.services?.find(
      ({ id }) => id === (activeStep.value?.provider || null)
    )
  );

  const activeStepProviderDetails = computed<
    Record<string, IAttribute[]> | undefined
  >(() => {
    if (activeStepProvider.value) {
      return serviceInfo.value?.data_service_details?.[
        activeStepProvider.value.id
      ];
    }
    return undefined;
  });

  const activeConfigPanel = computed<ActiveConfigPanel | null>(
    () => getters["orchestration/activeConfigPanel"]
  );

  const activeTemplateSteps = computed<IStep[]>(
    () => getters["orchestration/activeTemplateSteps"] ?? []
  );

  const activeTemplateHasSteps = computed(
    () => !!activeTemplateSteps.value.length
  );

  const activeConfigPanelConditional =
    computed<IActiveConfigPanelConditional | null>(
      () => getters["orchestration/activeConfigPanelConditional"]
    );

  const deselectStep = () => {
    if (activeConfigPanel.value) {
      commit("orchestration/setActiveConfigPanel", "");
    }
    if (activeConfigPanelConditional.value) {
      commit("orchestration/setActiveConfigPanelConditional", null);
    }
    if (activeStep.value?.id) {
      commit("orchestration/setActiveStep", {});
    }
  };

  const activeTemplateErrors = computed(() => {
    if (!activeTemplateSteps.value?.length) {
      return {};
    }
    return validateDataOrchestrationPayload(activeTemplateSteps.value);
  });

  const activeTemplateErrorsFlattened = computed(() => {
    return Object.values(activeTemplateErrors.value).flatMap(
      ({ errors }) => errors
    );
  });

  const getStepOrOutcomePosition = (
    stepId: string,
    outcomeName?: DataOrchestrationOutcome
  ): Position2D => {
    const step = activeTemplateSteps.value.find((s) => s.id === stepId);
    if (!step) {
      return get2dEmptyPosition();
    }

    if (outcomeName) {
      const outcome = step.outcomes.find((o) => o.outcome === outcomeName);
      return outcome?.position ?? get2dEmptyPosition();
    }

    return step.position ?? get2dEmptyPosition();
  };

  const getPositionForNewStep = (
    stepId?: string | null,
    outcomeName?: DataOrchestrationOutcome
  ): Position2D => {
    const parentPosition = stepId
      ? getStepOrOutcomePosition(stepId, outcomeName)
      : get2dEmptyPosition();

    const posAdjustments = nodesGraphPositionAdjustments["step-dropzone"]!;

    const position: Position2D = stepId
      ? {
          x: parentPosition.x + posAdjustments.x - 5, // this slight correction of -5 is needed after the drop
          y: parentPosition.y + posAdjustments.y,
        }
      : parentPosition;

    return position;
  };

  const getPositionForNewOutcome = (stepId: string): Position2D => {
    const stepPosition = getStepOrOutcomePosition(stepId);
    const posAdjustments = nodesGraphPositionAdjustments["outcome-dropzone"]!;
    const step = activeTemplateSteps.value.find((s) => s.id === stepId);

    const basePosition = {
      x: stepPosition.x + posAdjustments.x + 60,
      y: stepPosition.y + posAdjustments.y - 6,
    };

    if (!step?.outcomes?.length) {
      return basePosition;
    }

    const existingOutcomePositions = step.outcomes.map((outcome) =>
      getStepOrOutcomePosition(stepId, outcome.outcome)
    );

    if (!existingOutcomePositions.length) {
      return basePosition;
    }

    const OUTCOME_HEIGHT = OUTCOME_NODE_HEIGHT;
    // Outcomes use max-content as width, but the manual review (the longest one) is almost 95px wide
    const OUTCOME_WIDTH = 95;
    // Using step node width as spacing because outcomes show dropzones for steps which
    // can overlap with each other if not properly spaced
    const SPACING_X = STEP_NODE_WIDTH;
    // Outcome's height is approx. 25px, but we'll use double just to have a proper layout
    const SPACING_Y = 50;

    const checkOverlap = (pos: Position2D): boolean => {
      return existingOutcomePositions.some((existingPos) => {
        const xOverlap =
          Math.abs(pos.x - existingPos.x) < OUTCOME_WIDTH + SPACING_X;
        const yOverlap =
          Math.abs(pos.y - existingPos.y) < OUTCOME_HEIGHT + SPACING_Y;

        return xOverlap && yOverlap;
      });
    };

    const adjustedPosition = { ...basePosition };

    if (checkOverlap(adjustedPosition)) {
      const adjustmentStep = OUTCOME_WIDTH + SPACING_X;

      const rightPosition = {
        ...basePosition,
        x: basePosition.x + adjustmentStep,
      };
      if (!checkOverlap(rightPosition)) {
        return rightPosition;
      }

      const leftPosition = {
        ...basePosition,
        x: basePosition.x - adjustmentStep,
      };
      if (!checkOverlap(leftPosition)) {
        return leftPosition;
      }

      const farRightPosition = {
        ...basePosition,
        x: basePosition.x + 2 * adjustmentStep,
      };
      if (!checkOverlap(farRightPosition)) {
        return farRightPosition;
      }
    }

    return adjustedPosition;
  };

  return {
    activeTemplate,
    deselectStep,
    activeStep,
    activeStepProvider,
    activeStepProviderDetails,
    activeTemplateSteps,
    activeTemplateHasSteps,
    serviceInfo,
    activeTemplateErrors,
    activeTemplateErrorsFlattened,
    getStepOrOutcomePosition,
    getPositionForNewStep,
    getPositionForNewOutcome,
  };
};

export const useDoActiveTemplateStatus = () => {
  const { activeTemplate } = useDataOrchestration();

  const isActiveTemplatePublished = computed(
    () => activeTemplate.value.status === DO_SAVE_TEMPLATE_MODES.publish
  );

  return { isActiveTemplatePublished };
};

export const useDoActiveTemplateTrigger = () => {
  const { activeTemplate } = useDataOrchestration();

  const trigger = computed(
    () => activeTemplate.value.trigger || DEFAULT_DO_TRIGGER
  );

  return { trigger };
};

export const useSentilinkConfidenceLevels = (
  type: Ref<
    | (typeof GREATER_TYPES_SSN_CONFIDENCE_LEVELS)[number]
    | (typeof LESSER_TYPES_SSN_CONFIDENCE_LEVELS)[number]
    | string
  >
) => {
  const sentilinkConfidenceLevels = computed(() => {
    if (
      LESSER_TYPES_SSN_CONFIDENCE_LEVELS.includes(
        type.value as (typeof LESSER_TYPES_SSN_CONFIDENCE_LEVELS)[number]
      )
    ) {
      return COMMON_OPTIONS_LESSER_SSN_CONFIDENCE_LEVELS;
    } else if (
      GREATER_TYPES_SSN_CONFIDENCE_LEVELS.includes(
        type.value as (typeof GREATER_TYPES_SSN_CONFIDENCE_LEVELS)[number]
      )
    ) {
      return COMMON_OPTIONS_GREATER_SSN_CONFIDENCE_LEVELS;
    } else {
      return ALL_OPTIONS_SSN_CONFIDENCE_LEVELS;
    }
  });

  return { sentilinkConfidenceLevels };
};

export const useTimeInBusinessOptions = () => {
  const { t } = useI18n();
  return {
    days: t("COMMON.DAYS"),
    months: t("COMMON.MONTHS"),
    years: t("COMMON.YEARS"),
  };
};

export const useYearsOptions = computed(() => {
  const MIN_YEAR = 2020;
  const MAX_YEAR = new Date().getFullYear() - 1;
  const years = {} as Record<number, number>;
  for (let year = MAX_YEAR; year >= MIN_YEAR; year--) {
    years[year] = year;
  }
  return years;
});

export const useAttributesOptions = () => {
  const { getters } = useStore();

  const attributeOptions = computed<IAttribute[]>(() => [
    ...getters["orchestration/appDataAttributes"],
    ...getters["orchestration/customAttributesAttributes"],
    ...getters["orchestration/activeServiceAttributes"],
  ]);
  return {
    attributeOptions,
  };
};

export const useAttributesOptionsMap = () => {
  const { getters } = useStore();

  const activeDataServices = computed(() => {
    const activeServices: IDataServices =
      getters["orchestration/activeServices"];
    return activeServices.all_data_services;
  });

  const activeOther = computed(() => {
    const activeServices: IDataServices | undefined =
      getters["orchestration/activeServices"];
    return activeServices?.other || [];
  });

  const dataAttributes = computed(() => {
    const result: Record<string, IServiceAttribute> = {};
    activeDataServices.value?.forEach((service: IDataService) => {
      Object.keys(service.data_service_details || []).forEach((serviceId) => {
        if (service.data_service_details) {
          const serviceName =
            service.services?.find(
              (service) => service.id.toString() === serviceId
            )?.name || "";

          Object.values(service.data_service_details[serviceId]).forEach(
            (attributes) =>
              attributes.forEach((attribute) => {
                result[attribute.id] = getServiceAttribute(
                  serviceName,
                  attribute
                );
              })
          );
        }
      });
      (service.attributes || []).forEach((attribute) => {
        const serviceName = service.services?.[0]?.name || "";
        result[attribute.id] = getServiceAttribute(serviceName, attribute);
      });
    });
    return result;
  });

  const otherAttributes = computed(() => {
    const result: Record<string, IServiceAttribute> = {};
    activeOther.value?.forEach((other: IDataService) => {
      if (other.title === SERVICE_TRIGGER) {
        return;
      }
      other.attributes?.forEach((attribute) => {
        result[attribute.id] = getServiceAttribute(other.title, attribute);
      });
    });
    return result;
  });

  const allAttributes = computed(() => ({
    ...dataAttributes.value,
    ...otherAttributes.value,
  }));

  const customAttributes = computed(() => {
    return activeOther.value.reduce<
      Array<IServiceAttribute & { custom_id: string }>
    >((result, { attributes, title }) => {
      if (!attributes || title !== SERVICE_CUSTOM_ATTRIBUTES) {
        return result;
      }
      result.push(
        ...attributes.map((attr) => ({
          ...getServiceAttribute(title, attr),
          custom_id: (attr.context?.value as { id: string }).id,
        }))
      );
      return result;
    }, []);
  });

  const findCustomServiceAttribute = (attrId: string) => {
    return customAttributes.value.find(({ custom_id }) => custom_id === attrId);
  };

  /**
   * Returns a map object in a form of [AttributeID]: ServiceID
   * Example:
   *  {
   *    "dnb_pi_l3_unexpected_error": 7,
   *    "dnb_unexpected_error": 7,
   *    "ekata_account_opening": 1,
   *    "ekata_address_risk": 1,
   *  }
   */
  const attributeToServiceMap = computed(() => {
    const serviceToAttributesMap = Object.values(
      activeDataServices.value || []
    ).reduce<Record<string, string[]>>(
      (serviceAccumulator, currrentService) => {
        const serviceDetails = currrentService.data_service_details || {};
        const serviceToAttributes = Object.keys(serviceDetails).reduce<
          Record<string, string[]>
        >((acc, serviceIdentifier) => {
          const allServiceAttributes = Object.values(
            serviceDetails[serviceIdentifier]
          ).flatMap((detail) => detail.map(({ id }) => id));

          acc[serviceIdentifier] = allServiceAttributes;
          return acc;
        }, {});

        Object.keys(serviceToAttributes).forEach((serviceIdentifier) => {
          const idExists = !!serviceAccumulator[serviceIdentifier];
          serviceAccumulator[serviceIdentifier] = idExists
            ? uniq([
                ...serviceAccumulator[serviceIdentifier],
                ...serviceToAttributes[serviceIdentifier],
              ])
            : serviceToAttributes[serviceIdentifier];
        });

        return serviceAccumulator;
      },
      {}
    );

    return Object.entries(serviceToAttributesMap).reduce<
      Record<string, number>
    >((attributeToService, [serviceIdentifier, attributes]) => {
      attributes.forEach((attribute) => {
        attributeToService[attribute] = Number(serviceIdentifier);
      });
      return attributeToService;
    }, {});
  });

  return {
    allAttributes,
    dataAttributes,
    otherAttributes,
    activeDataServices,
    findCustomServiceAttribute,
    customAttributes,
    attributeToServiceMap,
  };
};

export const useDataOrchestrationCanvas = (props: {
  selectMode: Ref<boolean>;
  draggable: Ref<IDoDraggable | undefined>;
  canvasMode: Ref<OrchestrationStructureType>;
}) => {
  const initialNode: DataOrchestrationNode = {
    id: "initial-step-dropzone",
    type: "step-dropzone",
    draggable: false,
    position: get2dEmptyPosition(),
  };

  const STEP_OUTCOME_NEUTRAL_TYPE = "outcome-neutral" as const;

  const idFactory = {
    step: (step: IStep) => step.id,
    outcome: (step: IStep, outcome: IDataOrchestrationFlowOutcome) =>
      `${step.id}-${extractLetters(outcome.outcome)}`,
    ["step-dropzone"]: (step: IStep, outcome: IDataOrchestrationFlowOutcome) =>
      `${step.id}-${extractLetters(outcome.outcome)}-step-dropzone`,
    ["outcome-dropzone"]: (step: IStep) =>
      `${step.id}-${
        props.draggable.value?.id
          ? extractLetters(props.draggable.value.id!)
          : STEP_OUTCOME_NEUTRAL_TYPE
      }-outcome-dropzone`,
  };

  const FOLLOWS_THROUGH_OTHER_EDGE_ID = "follows-through-other" as const;

  const {
    onPaneClick,
    onNodeDragStop,
    onSelectionDragStop,
    nodes: canvasNodes,
    findNode,
  } = useVueFlow();
  const { commit } = useStore();
  const { t } = useI18n();
  const { deselectStep, activeTemplate, activeTemplateSteps } =
    useDataOrchestration();
  const { showMessage } = useNotification();

  const stepIdToAddOutcomeTo = ref<IStep["id"]>("");
  const intersectedNodeForOutcomeDrop = ref("");

  const isFreeform = computed(
    () => props.canvasMode.value === OrchestrationStructureType.Graph
  );

  const isTemplatePublished = computed(
    () => activeTemplate.value.status === OrchestrationTemplateStatus.Published
  );

  /** Edges represent the lines that connect rendered nodes */
  const edges = computed(() => {
    const edgeType = isFreeform.value ? "default" : "smoothstep";
    return activeTemplateSteps.value.reduce<VueFlowEdge[]>((edgesAcc, step) => {
      step.outcomes?.forEach((outcome) => {
        /** Each outcome has to be connected to a parenting step (service block) */
        const outcomeId = idFactory["outcome"](step, outcome);
        edgesAcc.push({
          id: outcomeId,
          source: step.id,
          target: outcomeId,
          type: edgeType,
        });

        if (outcome.next_step) {
          /** If there's a step linked to the outcome, connect it right away */
          const id = outcome.follows_other_outcome_path
            ? `${outcomeId}-${FOLLOWS_THROUGH_OTHER_EDGE_ID}-${outcome.next_step}`
            : `${outcomeId}-${outcome.next_step}`;
          edgesAcc.push({
            id,
            source: outcomeId,
            target: outcome.next_step,
            type: edgeType,
            markerEnd: MarkerType.ArrowClosed,
          });
        } else if (!isTemplatePublished.value) {
          /** Otherwise show a dropzone for a step */
          const stepDropzoneId = idFactory["step-dropzone"](step, outcome);
          edgesAcc.push({
            id: stepDropzoneId,
            source: outcomeId,
            target: stepDropzoneId,
            type: edgeType,
            animated: true,
          });
        }
      });

      if (canShowOutcomeDropzone(step)) {
        const outcomeDropzoneId = idFactory["outcome-dropzone"](step);

        edgesAcc.push({
          id: outcomeDropzoneId,
          source: step.id,
          target: outcomeDropzoneId,
          type: edgeType,
          animated: isDraggingAndIsOutcome.value || !!step.outcomes.length,
        });
      }

      return edgesAcc;
    }, []);
  });

  const nodeTargetToSourceMap = computed(() =>
    edges.value.reduce<Record<string, string>>((edgesAcc, edge) => {
      if (
        !edge.id.includes(FOLLOWS_THROUGH_OTHER_EDGE_ID) &&
        !edgesAcc[edge.target] &&
        edge.source
      ) {
        edgesAcc[edge.target] = edge.source;
      }
      return edgesAcc;
    }, {})
  );

  const nodes = computed(() => {
    /** Return just a step dropzone if there are no steps */
    if (!isTemplatePublished.value && !activeTemplateSteps.value.length) {
      return [initialNode];
    }

    const nodes: DataOrchestrationNode[] = activeTemplateSteps.value.reduce<
      DataOrchestrationNode[]
    >((nodesAcc, step) => {
      nodesAcc.push({
        id: step.id,
        type: "step",
        position: step.position ?? get2dEmptyPosition(),
        data: { step },
        zIndex: 10,
      });

      step.outcomes?.forEach((outcome) => {
        const outcomeId = `${step.id}-${extractLetters(outcome.outcome)}`;

        nodesAcc.push({
          id: outcomeId,
          type: "outcome",
          position: outcome.position ?? get2dEmptyPosition(),
          data: { step, outcome },
          zIndex: 20,
        });

        if (
          !isTemplatePublished.value &&
          !outcome.next_step &&
          outcome.outcome !== "manual review"
        ) {
          const stepDropzone: DataOrchestrationNode = {
            id: `${outcomeId}-step-dropzone`,
            type: "step-dropzone",
            draggable: false,
            position: get2dEmptyPosition(),
            data: { step, outcome },
            zIndex: 0,
          };

          if (isFreeform.value) {
            stepDropzone.parentNode = outcomeId;
            stepDropzone.expandParent = false;
            stepDropzone.extent = "parent";
            stepDropzone.position =
              nodesGraphPositionAdjustments["step-dropzone"];
          }

          nodesAcc.push(stepDropzone);
        }
      });

      if (canShowOutcomeDropzone(step)) {
        const outcomeId = idFactory["outcome-dropzone"](step);
        const outcomeDropzone: DataOrchestrationNode = {
          id: outcomeId,
          type: "outcome-dropzone",
          draggable: false,
          position: get2dEmptyPosition(),
          data: { step },
          zIndex: 0,
        };

        if (isFreeform.value) {
          outcomeDropzone.extent = "parent";
          outcomeDropzone.expandParent = false;
          outcomeDropzone.parentNode = step.id;
          outcomeDropzone.position =
            nodesGraphPositionAdjustments["outcome-dropzone"];
        }

        nodesAcc.push(outcomeDropzone);
      }

      return nodesAcc;
    }, []);

    if (isFreeform.value) {
      return nodes;
    }

    return getNodesInTreeLayout(nodes, nodeTargetToSourceMap.value);
  });

  const isDraggingAndIsOutcome = computed(() => {
    return (
      props.draggable.value?.isDragging &&
      props.draggable.value.type === DO_TYPE_CONDITIONAL
    );
  });

  const getNodesInTreeLayout = (
    nodes: DataOrchestrationNode[],
    nodeTargetToSourceMap: Record<string, string>
  ) => {
    /**
     * D3 layout doesn't provide a way assign dynamic node sizes
     * so we have to fine-tune the position manually.
     */

    const treeLayout = tree<DataOrchestrationNode>()
      .nodeSize([STEP_NODE_WIDTH, STEP_NODE_HEIGHT])
      .separation(() => 1.2);

    const hierarchy = stratify<DataOrchestrationNode>()
      .id((node) => node.id)
      .parentId((node) => {
        if (node.id === "initial-step-dropzone") {
          return undefined;
        }

        const step = node.data?.step;
        const outcome = node.data?.outcome;
        const targetId = idFactory[node.type!](step!, outcome!);
        return nodeTargetToSourceMap[targetId];
      });

    const hierarchyNode = hierarchy(nodes);
    const layout = treeLayout(hierarchyNode);
    return layout.descendants().map((node): DataOrchestrationNode => {
      let xPos = node.x;
      let yPos = node.y;
      const positionAdjustment =
        nodesStructuredPositionAdjustments[
          node.data.type as DataOrchestrationNodeType
        ];

      if (positionAdjustment) {
        xPos += positionAdjustment.x;
        yPos += positionAdjustment.y;
      }

      return {
        ...node.data,
        position: { x: xPos, y: yPos },
      };
    });
  };

  const canShowOutcomeDropzone = (step: IStep) => {
    if (!step.outcomes.length) {
      return true;
    }

    return (
      isDraggingAndIsOutcome.value &&
      step.id === intersectedNodeForOutcomeDrop.value &&
      !step.outcomes.some(
        ({ outcome }) => outcome === props.draggable.value?.id
      )
    );
  };

  const handleDragStopEvent = ({ nodes: allDraggedNodes }: NodeDragEvent) => {
    for (const draggedNode of allDraggedNodes) {
      const stepData: DataOrchestrationNode["data"] = draggedNode.data;
      const stepId = stepData?.step?.id;
      if (stepId) {
        const data: AddPositionToStepOrOutcomePayload = {
          stepId,
          outcome: stepData?.outcome?.outcome,
          position: draggedNode.position,
        };

        commit("orchestration/addPositionToStepOrOutcome", data);
      }
    }
  };

  onSelectionDragStop(handleDragStopEvent);
  onNodeDragStop(handleDragStopEvent);

  /**
   * Handles when a user draws a connection from an outcome to a step.
   * Sets the target step as the outcome's next_step directly (not using follows_other_outcome_path).
   * Note: An outcome can only have ONE next_step, but multiple outcomes can point to the same step.
   */
  const onHandleConnect = (connection: Connection) => {
    const sourceNode = findNode(connection.source);

    if (!sourceNode?.data?.step || !sourceNode?.data?.outcome) {
      return;
    }

    const sourceStepId = sourceNode.data.step.id;
    const targetStepId = connection.target;

    if (wouldCreateCycle(sourceStepId, targetStepId, edges.value)) {
      showMessage(t("ORCHESTRATION.CIRCULAR_CONNECTION_ERROR"), "warning");
      return;
    }

    commit("orchestration/addConnectionToOutcome", {
      stepId: sourceStepId,
      outcome: sourceNode.data.outcome.outcome,
      targetStepId,
    });
  };

  onPaneClick(() => {
    deselectStep();
    stepIdToAddOutcomeTo.value = "";
  });

  const handleKeyUp = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      deselectStep();
    }
  };

  onMounted(() => {
    window.addEventListener("keyup", handleKeyUp);
  });

  onUnmounted(() => {
    window.removeEventListener("keyup", handleKeyUp);
  });

  return {
    handleDragStopEvent,
    canvasNodes,
    isFreeform,
    idFactory,
    stepIdToAddOutcomeTo,
    canShowOutcomeDropzone,
    isDraggingAndIsOutcome,
    intersectedNodeForOutcomeDrop,
    isTemplatePublished,
    nodes,
    edges,
    nodeTargetToSourceMap,
    initialNode,
    FOLLOWS_THROUGH_OTHER_EDGE_ID,
    getNodesInTreeLayout,
    onHandleConnect,
  };
};

export interface UseToolbarOverflowDetectionOptions {
  dropdownRef: Ref<HTMLElement | null>;
  isVisible: Ref<boolean>;
  defaultPosition: Position.Top | Position.Bottom;
}

const SPACE_BUFFER = 20;
const NODE_HEIGHT_ESTIMATE_FLIP = 120;
const NODE_HEIGHT_ESTIMATE_UNFLIP = 60;

export const useToolbarOverflowDetection = (
  options: UseToolbarOverflowDetectionOptions
): ComputedRef<Position> => {
  const { dropdownRef, isVisible, defaultPosition } = options;
  const { viewport, vueFlowRef } = useVueFlow();

  const { height } = useElementSize(dropdownRef);
  const { top, bottom, update } = useElementBounding(dropdownRef);

  const currentPosition = ref(defaultPosition);

  const isOverflowingBottom = (canvasBottom: number) =>
    bottom.value + SPACE_BUFFER > canvasBottom;

  const isOverflowingTop = (canvasTop: number) =>
    top.value - SPACE_BUFFER < canvasTop;

  const canFlipToTop = (canvasTop: number) => {
    const estimatedTop = top.value - NODE_HEIGHT_ESTIMATE_FLIP - height.value;
    return estimatedTop - SPACE_BUFFER >= canvasTop;
  };

  const canFlipToBottom = (canvasBottom: number) => {
    const estimatedBottom =
      bottom.value + NODE_HEIGHT_ESTIMATE_FLIP + height.value;
    return estimatedBottom + SPACE_BUFFER <= canvasBottom;
  };

  const canUnflipToBottom = (canvasBottom: number) => {
    const estimatedBottom =
      bottom.value + NODE_HEIGHT_ESTIMATE_UNFLIP + height.value;
    return estimatedBottom + SPACE_BUFFER <= canvasBottom;
  };

  const canUnflipToTop = (canvasTop: number) => {
    const estimatedTop = top.value - NODE_HEIGHT_ESTIMATE_UNFLIP - height.value;
    return estimatedTop - SPACE_BUFFER >= canvasTop;
  };

  const checkPosition = () => {
    if (
      !isVisible.value ||
      height.value === 0 ||
      !dropdownRef.value ||
      !vueFlowRef.value
    ) {
      currentPosition.value = defaultPosition;
      return;
    }

    const canvasRect = vueFlowRef.value.getBoundingClientRect();
    const isAtDefaultPosition = currentPosition.value === defaultPosition;

    const positionConfig: Record<
      typeof options.defaultPosition,
      {
        opposite: typeof options.defaultPosition;
        shouldFlip: () => boolean;
        canUnflip: () => boolean;
      }
    > = {
      [Position.Bottom]: {
        opposite: Position.Top,
        shouldFlip: () =>
          isOverflowingBottom(canvasRect.bottom) &&
          canFlipToTop(canvasRect.top),
        canUnflip: () => canUnflipToBottom(canvasRect.bottom),
      },
      [Position.Top]: {
        opposite: Position.Bottom,
        shouldFlip: () =>
          isOverflowingTop(canvasRect.top) &&
          canFlipToBottom(canvasRect.bottom),
        canUnflip: () => canUnflipToTop(canvasRect.top),
      },
    };

    const config = positionConfig[defaultPosition];

    if (isAtDefaultPosition) {
      if (config.shouldFlip()) {
        currentPosition.value = config.opposite;
      }
    } else if (config.canUnflip()) {
      currentPosition.value = defaultPosition;
    }
  };

  const updateAndCheck = () => {
    update();
    checkPosition();
  };

  const debouncedCheck = useDebounceFn(() => {
    if (isVisible.value) {
      updateAndCheck();
    }
  }, 100);

  watch(viewport, debouncedCheck, { deep: true });

  watch(isVisible, (visible) => {
    if (visible) {
      updateAndCheck();
    } else {
      currentPosition.value = defaultPosition;
    }
  });

  watch(height, (newHeight, oldHeight) => {
    if (isVisible.value && oldHeight === 0 && newHeight > 0) {
      updateAndCheck();
    }
  });

  return computed(() => currentPosition.value);
};
